import { CSSProperties, FC, ReactNode } from 'react'
import classNames from 'classnames'
import { Tooltip } from 'antd'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import PerText from '../../../components/custom_components/Text'
import { EmployeeLevelEnum, PyramidType } from '../../../types'
import { PyramidsColumnGraphData, PyramidsColumnGraphSeries } from '../pyramidUtils'
import { PYRAMIDS_COLUMN_GRAPH_VARIANTS, PyramidsColumnGraphVariant, PyramidsColumnGraphVariantConfig } from './pyramidColumnGraphConfig'
import '../styles/pyramids-column-graph.less'

const Y_AXIS_TICKS = [100, 75, 50, 25, 0] as const

type SeriesKey = 'past' | 'current' | 'forecast'

type Props = {
  data: PyramidsColumnGraphData[]
  type: PyramidType
  variant: PyramidsColumnGraphVariant
  chartAriaLabel: string
  showPast?: boolean
  showCurrent?: boolean
  showForecast?: boolean
  /** Dashboard only: pyramid column keys selected as monitoring filters. */
  selectedLevelPill?: { label: string; value: EmployeeLevelEnum[] }
  onLevelLabelClick?: (levelKey: string) => void
}

const formatPercentLabel = (value: number): string => `${Math.round(value)}%`
const formatPercentNumber = (value: number): number => Math.round(value)
const formatSeriesTooltipValue = (series: PyramidsColumnGraphSeries): string => `${series.count} (${formatPercentLabel(series.percentage)})`

const blockClass = (config: PyramidsColumnGraphVariantConfig, element: string): string => `${config.classPrefix}__${element}`

const isCurrentOutsideRange = (current: number, range?: [number, number]): boolean => {
  if (!range) {
    return false
  }
  const [min, max] = range
  return current < min || current > max
}

const getColumnLabel = (name: string, type: PyramidType, t: TFunction): string => {
  if (type === PyramidType.Level) {
    return t(`employee.contract.level.${name}`)
  }
  return name
}

const formatRecommendedRangeLabel = (range: [number, number], t: TFunction): string => {
  const [min, max] = range
  const minRounded = Math.round(min)
  const maxRounded = Math.round(max)

  if (min === 0) {
    return `${t('pyramidsView.shared.overview.Max')} ${maxRounded}%`
  }
  if (max === 100) {
    return `${t('pyramidsView.shared.overview.Min')} ${minRounded}%`
  }
  return `${minRounded}% – ${maxRounded}%`
}

const getRecommendedRangeStyle = (range: [number, number], barWidthPx: number, trackHeightPx: number): CSSProperties => {
  const [min, max] = range
  const capRadiusPercent = (barWidthPx / 2 / trackHeightPx) * 100
  const span = max - min
  const heightPercent = Math.max(span + 2 * capRadiusPercent, (barWidthPx / trackHeightPx) * 100)

  return {
    bottom: `${min - capRadiusPercent}%`,
    height: `${heightPercent}%`,
  }
}

const getTooltipSeriesLabelKey = (variant: PyramidsColumnGraphVariant, series: SeriesKey): string => {
  if (variant === 'dashboard') {
    return series === 'current'
      ? 'dashboard.companyManager.filtersArea.pyramids.dotTooltip.current'
      : `pyramidsView.shared.legend.${series}`
  }
  return `pyramidsView.shared.legend.${series}`
}

type PyramidColumnTooltipContentProps = {
  columnLabel: string
  item: PyramidsColumnGraphData
  variant: PyramidsColumnGraphVariant
  showPast: boolean
  showCurrent: boolean
  showForecast: boolean
}

const PyramidColumnTooltipContent: FC<PyramidColumnTooltipContentProps> = ({
  columnLabel,
  item,
  variant,
  showPast,
  showCurrent,
  showForecast,
}) => {
  const { t } = useTranslation()
  const config = PYRAMIDS_COLUMN_GRAPH_VARIANTS[variant]
  const tooltipClass = blockClass(config, 'column-tooltip')
  const tooltipLevelClass = blockClass(config, 'column-tooltip-level')
  const tooltipRowClass = blockClass(config, 'column-tooltip-row')

  const seriesRows: { key: SeriesKey; value: PyramidsColumnGraphSeries }[] = []
  if (showPast && item.past) {
    seriesRows.push({ key: 'past', value: item.past })
  }
  if (showCurrent && item.current) {
    seriesRows.push({ key: 'current', value: item.current })
  }
  if (showForecast && item.forecast) {
    seriesRows.push({ key: 'forecast', value: item.forecast })
  }

  const recommendedLabelKey =
    variant === 'dashboard'
      ? 'dashboard.companyManager.filtersArea.pyramids.dotTooltip.recommended'
      : 'pyramidsView.shared.legend.recommended'

  return (
    <div className={tooltipClass}>
      <div className={tooltipLevelClass}>{columnLabel}</div>
      {seriesRows.map(({ key, value }) => (
        <div key={key} className={tooltipRowClass}>
          {t(getTooltipSeriesLabelKey(variant, key))}: {formatSeriesTooltipValue(value)}
        </div>
      ))}
      {item.recommendedRange && (
        <div className={tooltipRowClass}>
          {t(recommendedLabelKey)}: {formatRecommendedRangeLabel(item.recommendedRange, t)}
        </div>
      )}
    </div>
  )
}

type VisibleDot = {
  key: SeriesKey
  percentage: number
  zIndex: number
}

const getVisibleDots = (item: PyramidsColumnGraphData, showPast: boolean, showCurrent: boolean, showForecast: boolean): VisibleDot[] => {
  const dots: VisibleDot[] = []
  if (showPast && item.past) {
    dots.push({ key: 'past', percentage: item.past.percentage, zIndex: 1 })
  }
  if (showCurrent && item.current) {
    dots.push({ key: 'current', percentage: item.current.percentage, zIndex: 2 })
  }
  if (showForecast && item.forecast) {
    dots.push({ key: 'forecast', percentage: item.forecast.percentage, zIndex: 3 })
  }
  return dots
}

const buildColumnAriaLabel = (
  columnLabel: string,
  item: PyramidsColumnGraphData,
  showPast: boolean,
  showCurrent: boolean,
  showForecast: boolean,
): string => {
  const parts: string[] = [columnLabel]
  if (showPast && item.past) {
    parts.push(`past ${formatSeriesTooltipValue(item.past)}`)
  }
  if (showCurrent && item.current) {
    parts.push(`current ${formatSeriesTooltipValue(item.current)}`)
  }
  if (showForecast && item.forecast) {
    parts.push(`forecast ${formatSeriesTooltipValue(item.forecast)}`)
  }
  return parts.join(', ')
}

type PyramidColumnTrackProps = {
  item: PyramidsColumnGraphData
  columnLabel: string
  config: PyramidsColumnGraphVariantConfig
  variant: PyramidsColumnGraphVariant
  tooltip: ReactNode
  showPast: boolean
  showCurrent: boolean
  showForecast: boolean
}

const PyramidColumnTrack: FC<PyramidColumnTrackProps> = ({
  item,
  columnLabel,
  config,
  variant,
  tooltip,
  showPast,
  showCurrent,
  showForecast,
}) => {
  const visibleDots = getVisibleDots(item, showPast, showCurrent, showForecast)
  const outOfRange = config.highlightOutOfRange && item.current && isCurrentOutsideRange(item.current.percentage, item.recommendedRange)

  return (
    <div className={blockClass(config, 'column')}>
      <Tooltip title={tooltip} placement="top">
        <div
          className={blockClass(config, 'column-hit')}
          aria-label={buildColumnAriaLabel(columnLabel, item, showPast, showCurrent, showForecast)}
        >
          <div className={blockClass(config, 'track')} aria-hidden>
            {item.recommendedRange && (
              <div
                className={blockClass(config, 'range')}
                style={getRecommendedRangeStyle(item.recommendedRange, config.barWidthPx, config.trackHeightPx)}
              />
            )}
            {visibleDots.map(({ key, percentage, zIndex }) => (
              <div
                key={key}
                className={classNames(blockClass(config, 'dot'), {
                  [`${blockClass(config, 'dot')}--${key}`]: variant === 'pyramidsView',
                  [`${blockClass(config, 'dot')}--out-of-range`]: key === 'current' && outOfRange,
                })}
                style={{ bottom: `${percentage}%`, zIndex }}
              >
                {config.showDotValue && key === 'current' && item.current && (
                  <span className={blockClass(config, 'dot-value')}>{formatPercentNumber(item.current.percentage)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </Tooltip>
    </div>
  )
}

export const PyramidsColumnGraph: FC<Props> = ({
  data,
  type,
  variant,
  chartAriaLabel,
  showPast = false,
  showCurrent = true,
  showForecast = false,
  selectedLevelPill,
  onLevelLabelClick,
}) => {
  const { t } = useTranslation()
  const config = PYRAMIDS_COLUMN_GRAPH_VARIANTS[variant]

  if (!data.length) {
    return null
  }

  const formatXAxisLabel = (name: string): string => (variant === 'dashboard' ? name.replaceAll(' ', '') : name)
  const rootClassName = classNames(config.classPrefix, config.modifierClass)

  return (
    <div className={rootClassName} role="img" aria-label={chartAriaLabel}>
      <div className={blockClass(config, 'tracks-row')}>
        <div className={blockClass(config, 'y-axis')} aria-hidden>
          {Y_AXIS_TICKS.map((tick) => (
            <div key={tick} className={blockClass(config, 'y-tick')} style={{ bottom: `${tick}%` }}>
              {tick}%
            </div>
          ))}
        </div>

        <div className={blockClass(config, 'plot')}>
          <div className={blockClass(config, 'columns')}>
            {data.map((item) => {
              const columnLabel = getColumnLabel(item.name, type, t)
              return (
                <PyramidColumnTrack
                  key={item.name}
                  item={item}
                  columnLabel={columnLabel}
                  config={config}
                  variant={variant}
                  showPast={showPast}
                  showCurrent={showCurrent}
                  showForecast={showForecast}
                  tooltip={
                    <PyramidColumnTooltipContent
                      columnLabel={columnLabel}
                      item={item}
                      variant={variant}
                      showPast={showPast}
                      showCurrent={showCurrent}
                      showForecast={showForecast}
                    />
                  }
                />
              )
            })}
          </div>
        </div>
      </div>

      <div className={blockClass(config, 'labels-row')}>
        <div className={blockClass(config, 'y-axis-spacer')} aria-hidden />
        <div className={blockClass(config, 'labels')}>
          {data.map((item) => {
            const isSelected = selectedLevelPill?.label === item.name
            const labelClassName = classNames(blockClass(config, 'label'), {
              [`${blockClass(config, 'label')}--clickable`]: !!onLevelLabelClick,
              [`${blockClass(config, 'label')}--selected`]: isSelected,
            })
            const labelContent = <PerText color={isSelected ? 'white' : 'default'}>{formatXAxisLabel(item.name)}</PerText>

            if (onLevelLabelClick) {
              return (
                <button
                  key={item.name}
                  type="button"
                  className={labelClassName}
                  title={item.name}
                  aria-pressed={isSelected}
                  onClick={() => onLevelLabelClick(item.name)}
                >
                  {labelContent}
                </button>
              )
            }

            return (
              <div key={item.name} className={labelClassName} title={item.name}>
                {labelContent}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
